import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LANGS, LANG_LABELS, subscribeAll } from './model';
import { PrintSheet } from './MenuAdmin';
import { isBordo } from '../theme';
import './menu.css';

// Public A4 menu (the same sheet as the kitchen's Baskı tab). Linked from the
// confirmation email; guests can save it as PDF with the browser's print dialog.
const T = {
  tr: { pdf: 'PDF indir', print: 'Yazdır', making: 'Hazırlanıyor…', back: 'Menüye dön', loading: 'Menü hazırlanıyor…' },
  en: { pdf: 'Download PDF', print: 'Print', making: 'Preparing…', back: 'Back to menu', loading: 'Preparing the menu…' },
  fr: { pdf: 'Télécharger le PDF', print: 'Imprimer', making: 'Préparation…', back: 'Retour à la carte', loading: 'Préparation de la carte…' },
  es: { pdf: 'Descargar PDF', print: 'Imprimir', making: 'Preparando…', back: 'Volver a la carta', loading: 'Preparando la carta…' },
};

const isIOS = () => /iPhone|iPad|iPod/.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// Build the PDF from the hidden, unscaled copy of the sheet: one A4 page per
// .lm-sheet-page (front and back).
const buildPdf = async (box) => {
  if (document.fonts?.ready) await document.fonts.ready;
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import('html2canvas'), import('jspdf')]);
  const dark = isBordo();
  const bg = dark ? '#1d1819' : '#fbf8f2';
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pages = [...box.querySelectorAll('.lm-sheet-page')];
  for (let i = 0; i < pages.length; i += 1) {
    const canvas = await html2canvas(pages[i], {
      scale: 2, backgroundColor: bg, useCORS: true, scrollX: 0, scrollY: 0, windowWidth: 1200,
      onclone: (doc) => {
        const c = doc.querySelector('.lm-capture');
        if (c) Object.assign(c.style, { visibility: 'visible', overflow: 'visible', width: 'auto', height: 'auto' });
      },
    });
    if (i) pdf.addPage();
    // Fill the whole page in the sheet colour so a short page has no band.
    if (dark) pdf.setFillColor(29, 24, 25); else pdf.setFillColor(251, 248, 242);
    pdf.rect(0, 0, 210, 297, 'F');
    const h = Math.min(297, (canvas.height * 210) / canvas.width);
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.9), 'JPEG', 0, 0, 210, h);
  }
  return pdf.output('blob');
};

// Hand the file over right inside the tap: phones block downloads that start
// seconds later. iPhone gets the share sheet (Save to Files); where a download
// is not possible the PDF opens on screen.
const deliver = (blob, name) => {
  const file = typeof File === 'function' ? new File([blob], name, { type: 'application/pdf' }) : null;
  if (isIOS() && file && navigator.canShare?.({ files: [file] })) {
    navigator.share({ files: [file] }).catch((e) => {
      if (e?.name !== 'AbortError') window.location.href = URL.createObjectURL(blob);
    });
    return;
  }
  const url = URL.createObjectURL(blob);
  if (isIOS()) { window.location.href = url; return; }
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const PrintPage = () => {
  const [params, setParams] = useSearchParams();
  const lang = LANGS.includes(params.get('lang')) ? params.get('lang') : 'tr';
  const t = T[lang];
  const [data, setData] = useState(null);
  const wrap = useRef(null);
  const sheet = useRef(null);
  const capture = useRef(null);
  const [scale, setScale] = useState(1);
  const [height, setHeight] = useState(null);
  const [pdf, setPdf] = useState(null); // built in the background for this data/lang/menu
  const [making, setMaking] = useState(false);
  const fileName = `Lucien14-menu-${lang}.pdf`;
  const menuId = params.get('menu') || data?.config?.liveMenuId;

  // Prepare the PDF as soon as the menu is on screen, so the button works at once.
  useEffect(() => {
    if (!data) return undefined;
    let alive = true;
    const timer = setTimeout(() => {
      if (!capture.current) return;
      buildPdf(capture.current)
        .then((blob) => { if (alive) setPdf({ data, lang, menuId, blob }); })
        .catch((e) => console.error(e));
    }, 600);
    return () => { alive = false; clearTimeout(timer); };
  }, [data, lang, menuId]);

  const ready = !!(pdf && pdf.data === data && pdf.lang === lang && pdf.menuId === menuId);

  const downloadPdf = async () => {
    if (ready) { deliver(pdf.blob, fileName); return; }
    setMaking(true);
    try {
      deliver(await buildPdf(capture.current), fileName);
    } catch (e) {
      console.error(e);
      window.print();
    } finally {
      setMaking(false);
    }
  };

  useEffect(() => subscribeAll(setData, () => {}), []);
  useEffect(() => { document.title = 'Lucien14 · Menü'; }, []);

  // Scale the A4 sheet down to the screen width (phones); print is unscaled.
  useEffect(() => {
    if (!data) return undefined;
    const measure = () => {
      if (!wrap.current || !sheet.current) return;
      const s = Math.min(1, wrap.current.clientWidth / sheet.current.offsetWidth);
      setScale(s);
      setHeight(sheet.current.offsetHeight * s);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrap.current);
    ro.observe(sheet.current);
    return () => ro.disconnect();
  }, [data]);

  return (
    <div className="lm" style={{ minHeight: '100vh' }}>
      <div className="lm-admin-top lm-no-print">
        <div className="lm-admin-bar">
          <Link to="/menu" className="lm-btn lm-btn--ghost">← {t.back}</Link>
          <div className="lm-langs" style={{ margin: '0 auto' }}>
            {LANGS.map((l) => (
              <button key={l} type="button" className="lm-lang" aria-pressed={l === lang}
                onClick={() => { params.set('lang', l); setParams(params, { replace: true }); }}>{LANG_LABELS[l]}</button>
            ))}
          </div>
          <div className="lm-row" style={{ gap: 8 }}>
            <button type="button" className="lm-btn" onClick={() => window.print()}>{t.print}</button>
            <button type="button" className="lm-btn lm-btn--primary" disabled={making || !ready} onClick={downloadPdf}>{making || !ready ? t.making : t.pdf}</button>
          </div>
        </div>
      </div>
      <div className="lm-admin">
        {!data ? <div className="lm-status">{t.loading}</div> : (
          <div className="lm-sheet-wrap" ref={wrap} style={{ height: height ?? undefined }}>
            <div className="lm-sheet-scale" style={{ transform: `scale(${scale})` }}>
              <PrintSheet ref={sheet} data={data} menuId={menuId} lang={lang} />
            </div>
          </div>
        )}
        {data && (
          <div className="lm-capture" aria-hidden="true">
            <PrintSheet ref={capture} data={data} menuId={menuId} lang={lang} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PrintPage;
